import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  FaCheck,
  FaTimes,
  FaEdit,
  FaSave,
  FaUsers,
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaSync,
  FaFilter,
  FaChevronLeft,
  FaChevronRight,
  FaTasks,
} from "react-icons/fa";
import PageHeader from "../../core/common/header/PageHeader";
import {
  CourseEnrollment,
  StepProgressUpdateRequest,
} from "../../feature-module/interface/progress";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

interface StudentStepProgress {
  enrollmentId: number;
  studentId: number;
  studentName: string;
  studentEmail: string;
  courseTitle: string;
  stepName: string;
  isCompleted: boolean;
  completedBy?: string;
  completedAt?: string;
  notes?: string;
}

const COURSE_STEPS: Record<string, string[]> = {
  "Ausbildung Preparatory Course": [
    "Eligibility Check",
    "Profile Evaluation & Field Selection",
    "German Language Requirements Overview",
    "B1 Exam Preparation",
    "Application Documents Overview",
    "CV Standards Demonstration",
    "CV Draft Review",
    "CV Final Review",
    "Cover Letter Standards",
    "Cover Letter Draft Review",
    "Cover Letter Final Review",
    "Professional Photo Guidelines",
    "Photo Review & Approval",
    "Email Writing Standards",
    "Email Draft Review",
    "Ausbildung Search Strategies",
    "Ausbildung Portal Navigation",
    "Company Research Techniques",
    "Ausbildung Application Submission",
    "Ongoing Application Assessment",
    "Application Tracking & Follow-up",
    "Pre-Interview Email Communication",
    "Interview Scheduling & Confirmation",
    "Pre-Interview Preparation",
    "Ausbildung Interview Preparation",
    "Mock Interview Sessions",
    "Post-Interview Follow-up Email",
    "Post-Interview Communication",
    "Contract Negotiation Support",
    "Visa Appointment Application",
    "Visa Requirements Checklist",
    "Visa Documents Preparation",
    "Visa Document Review",
    "Visa Document Submission",
    "Visa Interview Preparation",
    "Mock Visa Interview",
    "Getting Ausbildung Visa",
    "After Visa Support & Guidance",
  ],
  "Ausbildung Preparatory Course (Without German Language)": [
    "Eligibility Check",
    "Application Documents Overview",
    "CV Standards Demonstration",
    "CV Draft Review",
    "CV Final Review",
    "Cover Letter Standards",
    "Cover Letter Draft Review",
    "Cover Letter Final Review",
    "Professional Photo Guidelines",
    "Photo Review & Approval",
    "Email Writing Standards",
    "Email Draft Review",
    "Ausbildung Search Strategies",
    "Ausbildung Portal Navigation",
    "Company Research Techniques",
    "Ausbildung Application Submission",
    "Ongoing Application Assessment",
    "Application Tracking & Follow-up",
    "Pre-Interview Email Communication",
    "Interview Scheduling & Confirmation",
    "Pre-Interview Preparation",
    "Ausbildung Interview Preparation",
    "Mock Interview Sessions",
    "Post-Interview Follow-up Email",
    "Post-Interview Communication",
    "Contract Negotiation Support",
    "Visa Appointment Application",
    "Visa Requirements Checklist",
    "Visa Documents Preparation",
    "Visa Document Review",
    "Visa Document Submission",
    "Visa Interview Preparation",
    "Mock Visa Interview",
    "Getting Ausbildung Visa",
    "After Visa Support & Guidance",
  ],
  "Student Visa Processing Course": [
    "General Information & Profile Evaluation",
    "Primary Documentation Fulfillment Check",
    "Review Final Documentation",
    "Understanding Admission Requirements",
    "Finding Course, University & City",
    "Understanding University Requirements",
    "Making Shortlist of Universities",
    "CV Standards Demonstration",
    "Review CV Draft Version",
    "Review CV Final Version",
    "Cover Letter Standards",
    "Review Cover Letter Draft Version",
    "Review Cover Letter Final Version",
    "Submitting University Required Documents",
    "University Application",
    "Support in Communication with Uni-Assist & Universities",
    "Check Visa Requirements",
    "Blocked Account Setup",
    "Health Insurance",
    "Visa Application Preparation",
    "Finding Accommodation",
    "Visa Interview Preparation",
    "Visa Interview",
    "After Visa Guidance",
    "Our Tips for Your New Journey",
  ],
  "Ausbildung Searching Visa Processing Course": [
    "Eligibility Check & Profile Evaluation",
    "German Language B1 Skill Test",
    "Understanding Visa Requirements",
    "Visa Documents Checklist",
    "Visa Documents Preparation",
    "Document Review & Verification",
    "Motivation Letter for Visa",
    "Motivation Letter Review",
    "Financial Proof Preparation",
    "Blocked Account Setup",
    "Health Insurance Arrangement",
    "Visa Appointment Application",
    "Visa Application Form Completion",
    "Document Organization for Submission",
    "Visa Document Submission",
    "Visa Interview Preparation",
    "Mock Visa Interview",
    "Common Visa Questions Practice",
    "Getting Ausbildung Searching Visa",
    "After Visa Guidance",
    "Accommodation Search Support",
    "Registration in Germany (Anmeldung)",
    "Opening Bank Account in Germany",
    "Tips for Your Ausbildung Search in Germany",
  ],
  "Combo: Ausbildung Searching and Preparatory Course": [
    "Eligibility Check",
    "Profile Evaluation & Field Selection",
    "German Language B1 Skill Test",
    "Financial Proof Preparation",
    "Visa Documents Preparation",
    "Motivation Letter for Visa",
    "Visa Appointment Application",
    "Visa Document Submission",
    "Visa Interview Preparation",
    "Getting Ausbildung Searching Visa",
    "After Visa Guidance & Registration",
    "Application Documents Overview",
    "CV Standards for Ausbildung",
    "CV Review",
    "Cover Letter Standards for Ausbildung",
    "Cover Letter Review",
    "Professional Photo Guidelines",
    "Email Writing Draft",
    "Email Writing Review",
    "Ausbildung Search Strategies",
    "Ausbildung Application Submission",
    "Ongoing Application Assessment",
    "Pre-Interview Preparation",
    "Ausbildung Interview Preparation",
    "Post-Interview Communication",
    "Get Ausbildung Contract",
  ],
};

const AdminStepManagement: React.FC = () => {
  const [enrollments, setEnrollments] = useState<CourseEnrollment[]>([]);
  const [stepProgress, setStepProgress] = useState<StudentStepProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // Filters and search
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("");
  const [completionFilter, setCompletionFilter] = useState<string>("all");

  // ✅ Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Edit mode
  const [editingNote, setEditingNote] = useState<string>("");
  const [editingNotes, setEditingNotes] = useState<Record<string, string>>({});

  // Expandable rows
  const [expandedEnrollments, setExpandedEnrollments] = useState<Set<number>>(
    new Set()
  );

  // ✅ OPTIMIZED: Fetch data with single bulk call
  const fetchData = async () => {
    try {
      setLoading(true);
      setError("");

      // Fetch enrollments
      const enrollmentsRes = await fetch(`${API_BASE}/api/admin/enrollments`, {
        credentials: "include",
      });

      if (!enrollmentsRes.ok) throw new Error("Failed to fetch enrollments");

      const allEnrollments: CourseEnrollment[] = await enrollmentsRes.json();

      // Filter to non-German active enrollments
      const nonGermanEnrollments = allEnrollments.filter((enrollment) => {
        const courseTitle = enrollment.courseTitle;
        return (
          Object.keys(COURSE_STEPS).some((course) =>
            courseTitle.includes(course)
          ) && enrollment.status === "ACTIVE"
        );
      });

      setEnrollments(nonGermanEnrollments);

      // ✅ OPTIMIZED: Fetch ALL progress in ONE call
      const progressRes = await fetch(
        `${API_BASE}/api/admin/all-step-progress`,
        {
          credentials: "include",
        }
      );

      if (progressRes.ok) {
        const allProgress: StudentStepProgress[] = await progressRes.json();

        // Filter to only relevant enrollments
        const relevantEnrollmentIds = new Set(
          nonGermanEnrollments.map((e) => e.id)
        );

        const filteredProgress = allProgress.filter((progress) =>
          relevantEnrollmentIds.has(progress.enrollmentId)
        );

        setStepProgress(filteredProgress);
      } else {
        console.warn("Failed to fetch step progress");
        setStepProgress([]);
      }

      setLastUpdated(new Date());
    } catch (err: any) {
      console.error("Failed to fetch data:", err);
      setError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ✅ OPTIMIZED: Update with optimistic UI update
  const updateStepProgress = async (
    enrollmentId: number,
    stepName: string,
    isCompleted: boolean,
    notes?: string
  ) => {
    const updateKey = `${enrollmentId}-${stepName}`;
    setSaving(updateKey);
    setError("");
    setSuccess("");

    // ✅ Optimistic update
    setStepProgress((prev) => {
      const existing = prev.find(
        (sp) => sp.enrollmentId === enrollmentId && sp.stepName === stepName
      );

      if (existing) {
        return prev.map((sp) =>
          sp.enrollmentId === enrollmentId && sp.stepName === stepName
            ? {
                ...sp,
                isCompleted,
                notes,
                completedAt: isCompleted ? new Date().toISOString() : undefined,
              }
            : sp
        );
      } else {
        const enrollment = enrollments.find((e) => e.id === enrollmentId);
        if (!enrollment) return prev;

        return [
          ...prev,
          {
            enrollmentId,
            studentId: enrollment.id,
            studentName: `${enrollment.studentFirstName} ${enrollment.studentLastName}`,
            studentEmail: enrollment.studentEmail || "",
            courseTitle: enrollment.courseTitle || "",
            stepName,
            isCompleted,
            notes,
            completedAt: isCompleted ? new Date().toISOString() : undefined,
            completedBy: "Admin",
          },
        ];
      }
    });

    try {
      const request: StepProgressUpdateRequest = {
        enrollmentId,
        stepName,
        isCompleted,
        notes: notes || "",
      };

      const response = await fetch(`${API_BASE}/api/admin/step-progress`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`Failed to update step: ${response.status}`);
      }

      setSuccess(`Step "${stepName}" updated successfully`);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: any) {
      console.error("Failed to update step:", err);
      setError(err.message || "Failed to update step");

      // ✅ Revert optimistic update on error
      await fetchData();
    } finally {
      setSaving("");
    }
  };

  // ✅ OPTIMIZED: Memoize step progress lookup
  const progressMap = useMemo(() => {
    const map = new Map<string, StudentStepProgress>();
    stepProgress.forEach((sp) => {
      const key = `${sp.enrollmentId}-${sp.stepName}`;
      map.set(key, sp);
    });
    return map;
  }, [stepProgress]);

  const getStepProgress = useCallback(
    (
      enrollmentId: number,
      stepName: string
    ): StudentStepProgress | undefined => {
      return progressMap.get(`${enrollmentId}-${stepName}`);
    },
    [progressMap]
  );

  // ✅ OPTIMIZED: Memoize course steps
  const getCourseSteps = useCallback((courseTitle: string): string[] => {
    const matchingCourse = Object.keys(COURSE_STEPS).find((course) =>
      courseTitle.includes(course)
    );
    return matchingCourse ? COURSE_STEPS[matchingCourse] : [];
  }, []);

  // ✅ OPTIMIZED: Memoize completion counts
  const completionCounts = useMemo(() => {
    const counts = new Map<number, { completed: number; total: number }>();

    enrollments.forEach((enrollment) => {
      const courseSteps = getCourseSteps(enrollment.courseTitle);
      const totalSteps = courseSteps.length;
      const completedSteps = courseSteps.filter(
        (step) => getStepProgress(enrollment.id, step)?.isCompleted
      ).length;

      counts.set(enrollment.id, {
        completed: completedSteps,
        total: totalSteps,
      });
    });

    return counts;
  }, [enrollments, getCourseSteps, getStepProgress]);

  const getCompletedStepsCount = useCallback(
    (enrollmentId: number): number => {
      return completionCounts.get(enrollmentId)?.completed || 0;
    },
    [completionCounts]
  );

  const getCompletionPercentage = useCallback(
    (enrollmentId: number): number => {
      const counts = completionCounts.get(enrollmentId);
      if (!counts || counts.total === 0) return 0;
      return Math.round((counts.completed / counts.total) * 100);
    },
    [completionCounts]
  );

  // ✅ OPTIMIZED: Debounced search
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // ✅ Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, selectedCourse, completionFilter]);

  // ✅ OPTIMIZED: Memoize filtered enrollments
  const filteredEnrollments = useMemo(() => {
    return enrollments.filter((enrollment) => {
      const matchesSearch =
        `${enrollment.studentFirstName} ${enrollment.studentLastName}`
          .toLowerCase()
          .includes(debouncedSearchTerm.toLowerCase()) ||
        enrollment.studentEmail
          .toLowerCase()
          .includes(debouncedSearchTerm.toLowerCase()) ||
        enrollment.courseTitle
          .toLowerCase()
          .includes(debouncedSearchTerm.toLowerCase());

      const matchesCourse =
        selectedCourse === "" ||
        enrollment.courseTitle.includes(selectedCourse);

      const completionPercentage = getCompletionPercentage(enrollment.id);
      let matchesCompletion = true;

      if (completionFilter === "not-started") {
        matchesCompletion = completionPercentage === 0;
      } else if (completionFilter === "in-progress") {
        matchesCompletion =
          completionPercentage > 0 && completionPercentage < 100;
      } else if (completionFilter === "completed") {
        matchesCompletion = completionPercentage === 100;
      } else if (completionFilter === "0-25") {
        matchesCompletion =
          completionPercentage >= 0 && completionPercentage <= 25;
      } else if (completionFilter === "26-50") {
        matchesCompletion =
          completionPercentage >= 26 && completionPercentage <= 50;
      } else if (completionFilter === "51-75") {
        matchesCompletion =
          completionPercentage >= 51 && completionPercentage <= 75;
      } else if (completionFilter === "76-99") {
        matchesCompletion =
          completionPercentage >= 76 && completionPercentage <= 99;
      }

      return matchesSearch && matchesCourse && matchesCompletion;
    });
  }, [
    enrollments,
    debouncedSearchTerm,
    selectedCourse,
    completionFilter,
    getCompletionPercentage,
  ]);

  // ✅ Pagination calculations
  const totalPages = Math.ceil(filteredEnrollments.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;

  // ✅ Paginated enrollments
  const paginatedEnrollments = useMemo(() => {
    return filteredEnrollments.slice(startIndex, endIndex);
  }, [filteredEnrollments, startIndex, endIndex]);

  const uniqueCourses = useMemo(
    () => Array.from(new Set(enrollments.map((e) => e.courseTitle))),
    [enrollments]
  );

  const toggleExpanded = useCallback((enrollmentId: number) => {
    setExpandedEnrollments((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(enrollmentId)) {
        newSet.delete(enrollmentId);
      } else {
        newSet.add(enrollmentId);
      }
      return newSet;
    });
  }, []);

  const saveNote = async (enrollmentId: number, stepName: string) => {
    const noteKey = `${enrollmentId}-${stepName}`;
    const note = editingNotes[noteKey] || "";
    const currentProgress = getStepProgress(enrollmentId, stepName);

    await updateStepProgress(
      enrollmentId,
      stepName,
      currentProgress?.isCompleted || false,
      note
    );

    setEditingNotes((prev) => {
      const newNotes = { ...prev };
      delete newNotes[noteKey];
      return newNotes;
    });
    setEditingNote("");
  };

  // ✅ Pagination handlers
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  // ✅ Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 7;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <FaSync className="animate-spin h-12 w-12 text-[#009fe3] mx-auto mb-4" />
          <p className="text-gray-600">Loading student progress...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaTasks />}
        title="Course Step Management"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Course Steps" },
        ]}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Success/Error Messages */}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border-l-4 border-green-500 text-green-700 rounded">
            <div className="flex items-center">
              <FaCheck className="mr-2" />
              <span>{success}</span>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
            <div className="flex items-center">
              <FaTimes className="mr-2" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <div className="flex items-center gap-2 mb-4">
            <FaFilter className="text-[#009fe3]" />
            <h3 className="font-semibold text-gray-900">Filters</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Students
              </label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, or course..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-[#009fe3] focus:border-[#009fe3]"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Course
              </label>
              <select
                value={selectedCourse}
                onChange={(e) => setSelectedCourse(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-[#009fe3] focus:border-[#009fe3]"
              >
                <option value="">All Courses</option>
                {uniqueCourses.map((course) => (
                  <option key={course} value={course}>
                    {course}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Completion
              </label>
              <select
                value={completionFilter}
                onChange={(e) => setCompletionFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-[#009fe3] focus:border-[#009fe3]"
              >
                <option value="all">All Students</option>
                <option value="not-started">Not Started (0%)</option>
                <option value="0-25">0% - 25%</option>
                <option value="26-50">26% - 50%</option>
                <option value="51-75">51% - 75%</option>
                <option value="76-99">76% - 99%</option>
                <option value="completed">Completed (100%)</option>
                <option value="in-progress">In Progress (1-99%)</option>
              </select>
            </div>
          </div>

          {/* Results Count & Page Size */}
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-sm text-gray-600">
              Showing{" "}
              <span className="font-semibold text-[#009fe3]">
                {filteredEnrollments.length === 0 ? 0 : startIndex + 1}-
                {Math.min(endIndex, filteredEnrollments.length)}
              </span>{" "}
              of{" "}
              <span className="font-semibold">
                {filteredEnrollments.length}
              </span>{" "}
              students
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Show:</label>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-[#009fe3] focus:border-[#009fe3]"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-gray-600">per page</span>
            </div>
          </div>
        </div>

        {/* Students and Steps */}
        <div className="space-y-4">
          {paginatedEnrollments.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <FaUsers className="text-4xl text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                No students found matching your criteria.
              </p>
            </div>
          ) : (
            paginatedEnrollments.map((enrollment) => {
              const courseSteps = getCourseSteps(enrollment.courseTitle);
              const isExpanded = expandedEnrollments.has(enrollment.id);
              const completedCount = getCompletedStepsCount(enrollment.id);
              const completionPercentage = getCompletionPercentage(
                enrollment.id
              );

              return (
                <div key={enrollment.id} className="bg-white rounded-lg shadow">
                  {/* Student Header */}
                  <div
                    className="p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => toggleExpanded(enrollment.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {enrollment.studentFirstName}{" "}
                            {enrollment.studentLastName}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {enrollment.studentEmail}
                          </p>
                          <p className="text-sm text-[#009fe3] font-medium">
                            {enrollment.courseTitle}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <div className="text-sm text-gray-600">
                            {completedCount} of {courseSteps.length} completed
                          </div>
                          <div className="text-xs text-gray-500">
                            {completionPercentage}%
                          </div>
                          <div className="w-32 bg-gray-200 rounded-full h-2 mt-1">
                            <div
                              className="bg-[#009fe3] h-2 rounded-full transition-all duration-300"
                              style={{
                                width: `${completionPercentage}%`,
                              }}
                            />
                          </div>
                        </div>

                        {isExpanded ? (
                          <FaChevronUp className="text-gray-400" />
                        ) : (
                          <FaChevronDown className="text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Course Steps */}
                  {isExpanded && (
                    <div className="p-4">
                      <div className="space-y-3">
                        {courseSteps.map((stepName, stepIndex) => {
                          const progress = getStepProgress(
                            enrollment.id,
                            stepName
                          );
                          const updateKey = `${enrollment.id}-${stepName}`;
                          const noteKey = `${enrollment.id}-${stepName}`;
                          const isSaving = saving === updateKey;
                          const isEditingNote = editingNote === noteKey;

                          return (
                            <div
                              key={stepIndex}
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                              <div className="flex items-center space-x-3 flex-1">
                                <button
                                  onClick={() =>
                                    updateStepProgress(
                                      enrollment.id,
                                      stepName,
                                      !progress?.isCompleted
                                    )
                                  }
                                  disabled={isSaving}
                                  className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                                    progress?.isCompleted
                                      ? "bg-green-500 text-white"
                                      : "bg-gray-200 hover:bg-gray-300"
                                  }`}
                                >
                                  {isSaving ? (
                                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                                  ) : progress?.isCompleted ? (
                                    <FaCheck className="text-xs" />
                                  ) : null}
                                </button>

                                <div className="flex-1">
                                  <div className="font-medium text-gray-900">
                                    {stepName}
                                  </div>
                                  {progress?.completedAt && (
                                    <div className="text-xs text-gray-500">
                                      Completed on{" "}
                                      {new Date(
                                        progress.completedAt
                                      ).toLocaleDateString()}
                                    </div>
                                  )}
                                  {progress?.notes && !isEditingNote && (
                                    <div className="text-xs text-gray-600 mt-1 italic">
                                      "{progress.notes}"
                                    </div>
                                  )}

                                  {isEditingNote && (
                                    <div className="mt-2">
                                      <textarea
                                        value={
                                          editingNotes[noteKey] ||
                                          progress?.notes ||
                                          ""
                                        }
                                        onChange={(e) =>
                                          setEditingNotes((prev) => ({
                                            ...prev,
                                            [noteKey]: e.target.value,
                                          }))
                                        }
                                        placeholder="Add notes about this step..."
                                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded resize-none focus:ring-[#009fe3] focus:border-[#009fe3]"
                                        rows={2}
                                      />
                                      <div className="flex space-x-2 mt-1">
                                        <button
                                          onClick={() =>
                                            saveNote(enrollment.id, stepName)
                                          }
                                          className="text-xs px-2 py-1 bg-[#009fe3] text-white rounded hover:bg-[#007cb5]"
                                        >
                                          <FaSave className="inline mr-1" />
                                          Save
                                        </button>
                                        <button
                                          onClick={() => {
                                            setEditingNote("");
                                            setEditingNotes((prev) => {
                                              const newNotes = { ...prev };
                                              delete newNotes[noteKey];
                                              return newNotes;
                                            });
                                          }}
                                          className="text-xs px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600"
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>

                              <button
                                onClick={() =>
                                  setEditingNote(isEditingNote ? "" : noteKey)
                                }
                                className="ml-2 p-1 text-gray-400 hover:text-gray-600"
                                title="Edit notes"
                              >
                                <FaEdit className="text-sm" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* ✅ Pagination Controls */}
        {filteredEnrollments.length > 0 && totalPages > 1 && (
          <div className="mt-6 bg-white rounded-lg shadow p-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Page Info */}
              <div className="text-sm text-gray-600">
                Page <span className="font-semibold">{currentPage}</span> of{" "}
                <span className="font-semibold">{totalPages}</span>
              </div>

              {/* Page Numbers */}
              <div className="flex items-center gap-2">
                {/* Previous Button */}
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Previous page"
                >
                  <FaChevronLeft className="w-4 h-4 text-gray-600" />
                </button>

                {/* Page Numbers */}
                <div className="flex items-center gap-1">
                  {getPageNumbers().map((page, index) =>
                    page === "..." ? (
                      <span
                        key={`ellipsis-${index}`}
                        className="px-3 py-2 text-gray-400"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => goToPage(page as number)}
                        className={`px-3 py-2 rounded-lg transition-colors ${
                          currentPage === page
                            ? "bg-[#009fe3] text-white font-semibold"
                            : "border border-gray-300 hover:bg-gray-50 text-gray-700"
                        }`}
                      >
                        {page}
                      </button>
                    )
                  )}
                </div>

                {/* Next Button */}
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Next page"
                >
                  <FaChevronRight className="w-4 h-4 text-gray-600" />
                </button>
              </div>

              {/* Jump to Page */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Go to:</span>
                <input
                  type="number"
                  min={1}
                  max={totalPages}
                  value={currentPage}
                  onChange={(e) => {
                    const page = Number(e.target.value);
                    if (page >= 1 && page <= totalPages) {
                      goToPage(page);
                    }
                  }}
                  className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm focus:ring-[#009fe3] focus:border-[#009fe3]"
                />
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminStepManagement;
